import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { setTitleBar } from "store/titleBar.slice";
import { useDispatch, useSelector } from "react-redux";
import type { courseType, stageType } from "models/course.model";
import "./course.scss";
import { ProgressBar } from "react-bootstrap";
import { CoursesService } from "services/courses.service";
import ScrollableSection, { type sectionDataType } from "components/scrollableSection/scrollableSection";
import { useScrollableSections } from "hook/useScrollableSections";
import { debounce, isEqual } from "lodash";
import Loader from "components/loader/loader";
import { useTranslation } from "react-i18next";


const Course = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState<boolean>();
    const [course, setCourse] = useState<courseType>();
    const [courseDetails, setCourseDetails] = useState<courseType>();
    const [stages, setStages] = useState<{ [key: string]: sectionDataType }>({});
    const prevDataRef = useRef(stages);
    const courseService = new CoursesService();
    const theme = useSelector((state: any) => state.theme);
    const { t } = useTranslation();

    const {
        seeMore,
        snapScroll,
        checkScrollableSections,
        containersRef,
    } = useScrollableSections(stages, setStages);

    const formatActivities = (stage: stageType)=> {
        const modules = stage.activities.map((act) => ({
            id: act.id,
            sessionId: act.courseSessionId,
            title: act.name,
            imageSrc: act.thumbnail,
            elements: act.elements,
            offline: false,
            type: "COURSE_ACTIVITY",
            status: act.status,
            mandatory: act.mandatory,
            order: act.order,
            sessionCreatedBy: act.sessionCreatedBy,
            stageOrder: stage.stageOrder
        }))

        const grains = stage.grains.map((grain) => ({
            id: grain.id,
            sessionId: grain.courseSessionId,
            title: grain.name,
            imageSrc: grain.thumbnail,
            offline: false,
            type: "COURSE_GRAIN",
            order: grain.order
        }))

        const all = [...modules, ...grains]

        return all

    }


    const transformStagesToSections = (stages: stageType[]): { [key: string]: sectionDataType } => {
        const result: { [key: string]: sectionDataType } = {};

        stages.forEach((stage) => {
            result[stage.name] = {
                sectionId: stage.name,
                sectionName: stage.name,
                activities: formatActivities(stage),
                isSeeMore: false,
                isScrollable: false,
                isLoading: false,
            };
        });
        return result;
    };

    const fetchData = async () => {
        if (!course) return;

        const courseSession = await courseService.getCourse(course);
        setCourseDetails(courseSession);
        const formatedCourseSession = transformStagesToSections(courseSession?.stages ?? []);
        setStages(formatedCourseSession)
    }

    useEffect(() => {
        dispatch(setTitleBar({
            title: "title_course",
            data: [],
            filteredData: [],
            isBackButton: true,
            isSearchBar: false,
            backButtonLabel: `course_back_${location.state?.from}`
        }));

        const handleResize = debounce(() => {
            checkScrollableSections();
        }, 300);

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            dispatch(setTitleBar({ title: "", data: [], filteredData: [] }));
        }
    }, [])

    useEffect(() => {
        setIsLoading(true);
        if (location.state?.course) {
            setCourse(location.state?.course);
        }
    }, [location.state]);

    useEffect(() => {
        fetchData();
    }, [course])

    useEffect(() => {
        setIsLoading(false);
        if (!isEqual(prevDataRef.current, stages)) {
            checkScrollableSections();
            prevDataRef.current = stages;
        }
    }, [stages])

    return (
        <div className="course">
            <div className="courseInformations">
                <div className="courseInformations__container">
                    <img src={course?.imageSrc} alt={course?.title}></img>
                    <div className="courseInformations__text">
                        <h4>{course?.title}</h4>
                        <p>{course?.description ?? courseDetails?.description}</p>
                        {course?.longDate && <p>{t("finish_before")} {course?.longDate}</p>}
                        <ProgressBar striped hidden={course?.progress === undefined} variant="warning" animated label={`${course?.progress}%`} now={course?.progress} />
                    </div>
                </div>
            </div>
            {stages && Object.keys(stages).length > 0 && (
                Object.entries(stages as Record<string, sectionDataType>).map(
                    ([key, stage], idx) => (

                        <ScrollableSection
                            ref={(el) => { containersRef.current[stage.sectionId] = el }}
                            id={stage.sectionId}
                            idx={idx}
                            key={idx}
                            title={stage.sectionName}
                            isScrollable={stage.isScrollable}
                            isSeeMore={stage.isSeeMore}
                            items={stage.activities}
                            onClose={fetchData}
                            onSeeMoreToggle={() => seeMore(stage.sectionId)}
                            onSnapScroll={snapScroll}
                            isMobile={theme.isTabletPortraitOrMobile}
                            isLoading={stage.isLoading}
                        />
                    )
                )
            )}
            {isLoading && (
                <Loader className={"loader_tertiary"} />
            )}
        </div>

    );
};

export default Course;
